import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLang, t } from '../../context/LanguageContext';
import T from '../../translations/index';
import logo from '../../assets/images/logo.png';
import './premiumheader.css';

const PremiumHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, toggleLang } = useLang();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location]);

  // Close menu on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleNav = useCallback((path) => {
    setMenuOpen(false);
    const routes = {
      home: '/', about: '/about', services: '/services',
      projects: '/projects', clients: '/clients',
      contact: '/contact', careers: '/careers', hireus: '/hireus',
    };
    if (routes[path]) {
      navigate(routes[path]);
    } else {
      const el = document.getElementById(path);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  }, [navigate]);

  const isActive = (path) => location.pathname === path;

  const navItems = [
    { key: 'home',     labelKey: 'navHome',     path: '/' },
    { key: 'about',    labelKey: 'navAbout',     path: '/about' },
    { key: 'services', labelKey: 'navServices',  path: '/services' },
    { key: 'projects', labelKey: 'navProjects',  path: '/projects' },
    { key: 'clients',  labelKey: 'navClients',   path: '/clients' },
    { key: 'contact',  labelKey: 'navContact',   path: '/contact' },
  ];

  return (
    <>
      {/* ── Top Info Bar ── */}
      <div className="ph-topbar" role="banner">
        <div className="ph-topbar-inner">
          <div className="ph-topbar-left">
            <a href="tel:+916200247745" className="ph-topbar-link" aria-label="Call us">
              <span className="ph-topbar-icon">📞</span>
              <span>+91 6200247745</span>
            </a>
            <a href="mailto:arunojha@trishivanay.com" className="ph-topbar-link" aria-label="Email us">
              <span className="ph-topbar-icon">✉️</span>
              <span>arunojha@trishivanay.com</span>
            </a>
          </div>
          <div className="ph-topbar-right">
            <span className="ph-topbar-cert">🇮🇳 DPIIT Startup</span>
            <span className="ph-topbar-cert ph-topbar-cert-teal">🏭 MSME Certified</span>
            {/* Language Toggle */}
            <button
              className="ph-lang-toggle"
              onClick={toggleLang}
              aria-label={lang === 'en' ? 'Switch to Hindi' : 'Switch to English'}
              title={lang === 'en' ? 'हिंदी में बदलें' : 'Switch to English'}
            >
              <span className={`ph-lang-opt ${lang === 'en' ? 'ph-lang-active' : ''}`}>EN</span>
              <span className="ph-lang-sep">|</span>
              <span className={`ph-lang-opt ${lang === 'hi' ? 'ph-lang-active' : ''}`}>हिं</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Main Header ── */}
      <header className={`ph-header${scrolled ? ' ph-header-scrolled' : ''}`} role="navigation">
        <div className="ph-header-inner">

          {/* Logo & Brand */}
          <button className="ph-brand" onClick={() => navigate('/')} aria-label="Go to homepage">
            <img src={logo} alt="TCPL Logo" className="ph-logo" />
            <div className="ph-brand-text">
              <span className="ph-brand-name">{t(T.companyName, lang)}</span>
              <span className="ph-brand-sub">{t(T.companySub, lang)}</span>
              <span className="ph-brand-tag">{t(T.tagline, lang)}</span>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="ph-nav" aria-label="Main navigation">
            <ul className="ph-nav-list">
              {navItems.map(item => (
                <li key={item.key}>
                  <button
                    className={`ph-nav-link${isActive(item.path) ? ' ph-nav-active' : ''}`}
                    onClick={() => handleNav(item.key)}
                    aria-current={isActive(item.path) ? 'page' : undefined}
                  >
                    {t(T[item.labelKey], lang)}
                  </button>
                </li>
              ))}
              <li>
                <button
                  className={`ph-nav-link ph-nav-pill ph-nav-careers${isActive('/careers') ? ' ph-nav-active' : ''}`}
                  onClick={() => handleNav('careers')}
                >
                  {t(T.navCareers, lang)}
                </button>
              </li>
              <li>
                <button
                  className={`ph-nav-link ph-nav-pill ph-nav-hire${isActive('/hireus') ? ' ph-nav-active' : ''}`}
                  onClick={() => handleNav('hireus')}
                >
                  {t(T.navHireUs, lang)}
                </button>
              </li>
            </ul>
          </nav>

          {/* Hamburger */}
          <button
            className={`ph-hamburger${menuOpen ? ' ph-hamburger-open' : ''}`}
            onClick={() => setMenuOpen(v => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span className="ph-ham-bar" />
            <span className="ph-ham-bar" />
            <span className="ph-ham-bar" />
          </button>
        </div>
      </header>

      {/* ── Mobile Drawer ── */}
      <div
        className={`ph-drawer-backdrop${menuOpen ? ' ph-drawer-backdrop-open' : ''}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      <aside className={`ph-drawer${menuOpen ? ' ph-drawer-open' : ''}`} aria-label="Mobile navigation">
        <div className="ph-drawer-brand">
          <img src={logo} alt="TCPL" className="ph-drawer-logo" />
          <div>
            <div className="ph-drawer-name">{t(T.companyName, lang)}</div>
            <div className="ph-drawer-sub">{t(T.companySub, lang)}</div>
          </div>
        </div>
        <nav>
          <ul className="ph-drawer-list">
            {navItems.map(item => (
              <li key={item.key}>
                <button
                  className={`ph-drawer-link${isActive(item.path) ? ' ph-drawer-link-active' : ''}`}
                  onClick={() => handleNav(item.key)}
                >
                  {t(T[item.labelKey], lang)}
                </button>
              </li>
            ))}
            <li>
              <button className="ph-drawer-link ph-drawer-pill-careers" onClick={() => handleNav('careers')}>
                {t(T.navCareers, lang)}
              </button>
            </li>
            <li>
              <button className="ph-drawer-link ph-drawer-pill-hire" onClick={() => handleNav('hireus')}>
                {t(T.navHireUs, lang)}
              </button>
            </li>
          </ul>
        </nav>
        <div className="ph-drawer-lang">
          <button className={`ph-drawer-lang-btn${lang==='en'?' active':''}`} onClick={() => lang !== 'en' && toggleLang()}>English</button>
          <button className={`ph-drawer-lang-btn${lang==='hi'?' active':''}`} onClick={() => lang !== 'hi' && toggleLang()}>हिंदी</button>
        </div>
      </aside>
    </>
  );
};

export default PremiumHeader;
